import type { MarketsApi } from "@/v2/api/metadata/markets";
import type { OutcomesApi } from "@/v2/api/metadata/outcomes";
import type { ContentTab, DialogTabSaveContext } from "@/v2/framework/dialog/types";
import { createInheritableTabSave } from "@/v2/kits/tenant-inheritance";
import { outcomePositionValidator, outcomeStatusOptions } from "../../../../shared";
import {
  buildOutcomeSupplementalNamesPatch,
  syncOutcomeSupplementalBaseline,
} from "../../outcome-fields";
import { notifySuccess, outcomeId } from "../shared";

const mainTabChildren: ContentTab["children"] = [
  {
    type: "text-field",
    name: "name",
    label: "Name",
    placeholder: "Enter name",
    inheritance: { computedKey: "computedName", parentKey: "parentName" },
    validate: (value, values) => {
      if (values.isTenantSourceOfEntity && !String(value ?? "").trim()) return "Required";
      return undefined;
    },
  },
  {
    type: "text-field",
    name: "eventMarketOutcomeName",
    label: "Event market outcome name",
    placeholder: "Enter event market outcome name",
    inheritance: {
      computedKey: "computedEventMarketOutcomeName",
      parentKey: "parentEventMarketOutcomeName",
    },
    validate: (value, values) => {
      if (values.isTenantSourceOfEntity && !String(value ?? "").trim()) return "Required";
      return undefined;
    },
  },
  {
    type: "select-field",
    name: "status",
    label: "Status",
    required: true,
    placeholder: "Select status",
    options: outcomeStatusOptions,
    inheritance: { computedKey: "computedStatus", parentKey: "parentStatus" },
  },
  {
    type: "text-field",
    name: "info",
    label: "Info",
    placeholder: "Enter info",
    inheritance: { computedKey: "computedInfo", parentKey: "parentInfo" },
  },
  {
    type: "text-field",
    name: "position",
    label: "Position",
    placeholder: "Enter position",
    validate: outcomePositionValidator,
    inheritance: { computedKey: "computedPosition", parentKey: "parentPosition" },
  },
];

export function createMainTab(outcomesApi: OutcomesApi, marketsApi: MarketsApi): ContentTab {
  const save = createInheritableTabSave({
    children: mainTabChildren,
    buildExtraPatch: (ctx) => buildOutcomeSupplementalNamesPatch(ctx.values, ctx.baseline),
    afterPersist: (ctx) => {
      syncOutcomeSupplementalBaseline(ctx);
    },
    persist: async (patch, ctx: DialogTabSaveContext) => {
      await outcomesApi.update(outcomeId(ctx.values), patch);
      notifySuccess();
    },
  });

  return {
    id: "main",
    label: "Main",
    save,
    children: [
      ...mainTabChildren,
      {
        type: "component",
        component: () => import("../../OutcomeSupplementalNamesFields.vue"),
        props: { mode: "edit" },
      },
      {
        type: "component",
        component: () => import("../../OutcomeSpecifiersFields.vue"),
        props: { mode: "edit", marketsApi },
      },
    ],
  };
}
